import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { SearchableSelect } from "../../components/SearchableSelect";
import { getCompanies, type Company } from "../../companies/companiesApi";
import {
  FinancialKpiCard,
  formatCurrency,
  formatPercent,
} from "../Obras/components/FinancialDashboardShared";
import {
  ApiError,
  getFinancialSummary,
  type FinancialSummary,
} from "../../resumo/resumoApi";

// O quadro "Resumo" do Excel de gestão, dentro do Financeiro: os cinco números anuais
// (faturado, despesa s/IVA, resultado, imposto, incidência) calculados sobre as faturas de
// receita e de despesa da app. O bloco de expectativas do Excel ficou de fora por decisão.

const FIRST_YEAR = 2022;

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401)
      return "Sessão expirada. Autentique-se novamente.";
    if (error.status === 403)
      return "Sem permissões para ver o resumo financeiro.";
    if (error.message) return error.message;
  }
  return "Não foi possível carregar o resumo financeiro.";
};

export function ResumoFinanceiroView() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // No primeiro carregamento mostra-se texto ("A carregar…") em vez da grelha esmaecida — o
  // esmaecimento sozinho não é anunciado por leitores de ecrã e "—" lê-se como "sem dados".
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    getCompanies()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setSummary(await getFinancialSummary(year, companyId));
    } catch (error) {
      setLoadError(parseErrorMessage(error));
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [year, companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= FIRST_YEAR; y -= 1) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  }, [currentYear]);

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({ value: company.id, label: company.name })),
    [companies],
  );

  const undatedNote = useMemo(() => {
    if (!summary) return null;
    const parts = [];
    if (summary.undatedRevenueCount > 0) {
      parts.push(`${summary.undatedRevenueCount} fatura(s) de receita`);
    }
    if (summary.undatedExpenseCount > 0) {
      parts.push(`${summary.undatedExpenseCount} despesa(s)`);
    }
    if (parts.length === 0) return null;
    return `${parts.join(" e ")} sem data de documento ficam fora do resumo — atribua a data para as incluir no ano.`;
  }, [summary]);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Resumo"
        subtitle="Resultado anual da empresa — faturação, despesa e impostos"
      />

      <div className="p-6 space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          {/* Largura em style inline: o Tailwind daqui é pré-compilado e w-40/w-72 não existem. */}
          <div style={{ minWidth: "10rem" }}>
            <SearchableSelect
              label="Ano"
              options={yearOptions}
              value={year}
              allowClear={false}
              onChange={(value) => setYear(value ?? currentYear)}
            />
          </div>
          <div style={{ minWidth: "18rem" }}>
            <SearchableSelect
              label="Empresa"
              placeholder="Todas"
              options={companyOptions}
              value={companyId}
              onChange={(value) => setCompanyId(value)}
            />
          </div>
        </div>

        {loadError && (
          <div
            role="alert"
            className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
          >
            {loadError}
          </div>
        )}

        {isLoading && !hasLoadedOnce && (
          <div className="p-8 text-center text-secondary-text">
            A carregar resumo financeiro…
          </div>
        )}

        <div
          aria-busy={isLoading}
          style={isLoading ? { opacity: 0.6 } : undefined}
          hidden={!hasLoadedOnce}
        >
          {/* Grid inline auto-ajustável: 5 cartões repartem a largura toda (grid-cols-5 não está
            no CSS pré-compilado) e quebram sozinhos quando o ecrã aperta. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
              gap: "1rem",
            }}
          >
            <FinancialKpiCard
              label="Total Faturado"
              value={summary ? formatCurrency(summary.invoicedNet) : "—"}
              hint="Faturas de receita (s/IVA)"
            />
            <FinancialKpiCard
              label="Total Despesa s/IVA"
              value={summary ? formatCurrency(summary.expenseNet) : "—"}
              hint="Faturas de despesa (s/IVA)"
            />
            <FinancialKpiCard
              label="Resultado Contabilístico"
              value={summary ? formatCurrency(summary.result) : "—"}
              hint="Faturado − despesa"
            />
            <FinancialKpiCard
              label="Impostos a Pagar do Resultado"
              value={summary ? formatCurrency(summary.tax) : "—"}
              hint={
                summary
                  ? `${formatPercent(summary.taxRate * 100)} do resultado`
                  : undefined
              }
            />
            <FinancialKpiCard
              label="Incidência sobre a faturação"
              value={summary ? formatPercent(summary.incidence * 100) : "—"}
              hint="Resultado ÷ faturado"
            />
          </div>
        </div>

        {undatedNote && (
          <p className="text-secondary-text text-sm">{undatedNote}</p>
        )}
      </div>
    </div>
  );
}
